import { createContext, type FC, type ReactNode, useContext, useMemo } from "react";

import { ProductionContext } from "@/contexts/production";
import useAxios from "@/api/use-axios";
import { baseRoutes } from "@/api/base-routes";
import { useCancellingQuery } from "@/hooks/use-cancelling-query";
import { isScanToTriggerSingleConfigured } from "@/views/configuration/production-groups/manual-scan-to-produce-dropdown";
import { type ActiveBatch } from "@/api/selection-api";

import { useActiveBatches } from "./query/active-batches";

interface JobQueueContextValue {
	selectedBatches: ActiveBatch[];
	jobHistory: unknown[];
	historyLoading: boolean;
	batchesLoading: boolean;
	refetchEnabled: boolean;
}

export const JobQueueContext = createContext<JobQueueContextValue>({
	selectedBatches: [],
	jobHistory: [],
	historyLoading: true,
	batchesLoading: true,
	refetchEnabled: true,
});

interface JobQueueProviderProps {
	readonly children: ReactNode;
}

export const JobQueueProvider: FC<JobQueueProviderProps> = ({ children }) => {
	const { currentMachineGroup, currentProductionGroup } = useContext(ProductionContext);
	const longPollingRateMs = 10000;

	const AuditPackagingSolutionsApi = useAxios(`${baseRoutes.audit}/v1/Audit/PackagingSolutions`);

	const scanToTriggerSingleConfigured = isScanToTriggerSingleConfigured(currentProductionGroup?.manualScanToProduce);

	const jobHistoryQueryFn = async () => {
		if (!currentMachineGroup) return [];
		const response = await AuditPackagingSolutionsApi.getWithUrl(
			`byMachineGroupHistory/${currentMachineGroup.id}/limit/10/latest`,
		);
		const data = response?.data as Array<{ data: string | unknown; createDateUTC: string }> | undefined;
		const jobHistory = (data || []).map((j) => ({
			...j,
			data: typeof j.data === "string" ? JSON.parse(j.data) : j.data,
			date: new Date(j.createDateUTC),
		}));
		return jobHistory;
	};

	const jobHistoryQueryKey = ["machineGroupJobHistory", { machineGroupId: currentMachineGroup?.id }];

	const {
		data: selectedBatches,
		isLoading: selectedBatchesApiLoading,
		refetchEnabled: selectedBatchesRefetchEnabled,
	} = useActiveBatches({
		machineGroupId: currentMachineGroup?.id,
		productionGroupId: currentProductionGroup?.id,
		scanToTriggerSingleConfigured,
	});

	const {
		data: jobHistory,
		isLoading: jobHistoryApiLoading,
		refetchEnabled: historyRefetchEnabled,
	} = useCancellingQuery({
		queryKey: jobHistoryQueryKey,
		queryFn: jobHistoryQueryFn,
		refetchInterval: longPollingRateMs,
		enabled: Boolean(currentMachineGroup),
	});

	const value = useMemo(
		() => ({
			jobHistory: jobHistory || [],
			selectedBatches: selectedBatches || [],
			historyLoading: jobHistoryApiLoading,
			batchesLoading: selectedBatchesApiLoading,
			refetchEnabled: selectedBatchesRefetchEnabled && historyRefetchEnabled,
		}),
		[historyRefetchEnabled, jobHistory, jobHistoryApiLoading, selectedBatches, selectedBatchesApiLoading],
	);

	if (!currentMachineGroup) {
		return children;
	}

	return <JobQueueContext.Provider value={value}>{children}</JobQueueContext.Provider>;
};
